import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/lib/i18n/LanguageProvider';

export type VoiceInputState = 'idle' | 'recording' | 'transcribing' | 'error';

interface TranscribeResponse {
  text: string;
  durationSeconds: number;
  costUsd: number;
}

/**
 * Records audio with MediaRecorder, POSTs the blob to /api/voice/transcribe,
 * returns the transcript. Single-recording state machine: idle → recording →
 * transcribing → idle. The caller decides what to do with the text.
 *
 * Voice is the primary way a tech puts anything into this product — gloves on,
 * hands dirty, under a truck — and it is the biggest payload the app sends over
 * the least reliable link. Until now a failed upload simply ended the story:
 * the clip existed only as a local `const` inside `stopAndTranscribe`, so the
 * moment that function returned, forty seconds of dictation was unreachable and
 * the tech was shown the browser's raw "Failed to fetch". The clip is kept now
 * so the same audio can be sent again.
 */
export interface TranscribeOutcome {
  /** Final text from Whisper, when it returned something usable. */
  text: string | null;
  /** Human-readable message for the UI to surface when text is null. */
  message?: string;
  /**
   * Words were spoken and we failed to write them down.
   *
   * This is the question the SEND guards ask, and it is not the same question
   * as "can they retry" — an expired session loses the words AND cannot be
   * retried. Conflating the two meant a non-retryable failure read as "safe to
   * send without the words", which is the exact bug being fixed one branch over.
   */
  lostWords?: boolean;
  /** The clip is still held — `retryTranscribe()` can send this exact audio again. */
  canRetry?: boolean;
}

/**
 * Below this, an empty transcript really is silence rather than a failure.
 *
 * Whisper returns "" for a loud bay, a low mic gain or a muted input just as
 * readily as for a quiet room, so judging by the TRANSCRIPT threw away real
 * forty-second dictations and told the tech to speak up. Judge by the clip.
 */
const PROBABLY_SILENCE_MS = 1200;

/** Whisper failures a tech can do something about, in words they can act on. */
function describeTranscribeFailure(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (/Failed to fetch|NetworkError|Load failed|ERR_INTERNET/i.test(raw)) {
    return "Couldn't reach the shop to write that down. Your recording is saved — try again.";
  }
  return "Couldn't turn that into words just now. Your recording is saved — try again.";
}

export function useVoiceInput(): {
  state: VoiceInputState;
  durationMs: number;
  error: string | null;
  /** True while a clip that failed to transcribe is still held. */
  hasHeldClip: boolean;
  start: () => Promise<void>;
  stopAndTranscribe: () => Promise<TranscribeOutcome>;
  /** Re-sends the held clip. Says so when there is nothing left to send. */
  retryTranscribe: () => Promise<TranscribeOutcome>;
  /**
   * Throws the held clip away. Surfaces call this when the thing they are
   * anchored to changes — the composers do NOT remount on a thread, channel or
   * truck switch, so a clip recorded against one unit would otherwise still be
   * retryable into another one's box.
   */
  discardClip: () => void;
  cancel: () => void;
} {
  // The language ON SCREEN, not `user.preferredLanguage`. The profile column
  // lags a tap by a whole round trip, so a tech who switched to Spanish and
  // immediately held the mic used to have his Spanish pinned as English.
  const { language } = useLanguage();
  const [state, setState] = useState<VoiceInputState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [durationMs, setDurationMs] = useState(0);
  const [hasHeldClip, setHasHeldClip] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /**
   * The clip that failed. Held so a retry sends the SAME audio.
   *
   * Cleared on success, on cancel, and at the start of a new recording — a
   * held clip that outlives its moment is the stale-state hazard this repo has
   * been bitten by before, and re-transcribing last truck's words into this
   * truck's composer would be exactly that.
   */
  const heldClipRef = useRef<{ blob: Blob; mime: string; durationMs: number } | null>(null);

  const releaseClip = useCallback(() => {
    heldClipRef.current = null;
    setHasHeldClip(false);
  }, []);

  const cleanup = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setDurationMs(0);
    releaseClip();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      tickRef.current = setInterval(() => {
        setDurationMs(Date.now() - startedAtRef.current);
      }, 200);
      recorder.start();
      setState('recording');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Microphone unavailable');
      setState('error');
      cleanup();
    }
  }, [cleanup, releaseClip]);

  const uploadClip = useCallback(
    async (blob: Blob, mime: string, durationMs: number): Promise<TranscribeOutcome> => {
      setState('transcribing');
      try {
        // `getSession()` is not a local read: inside the 90s expiry margin it
        // refreshes over the network, and on a failed refresh auth-js returns
        // no session while deliberately KEEPING the user signed in. Discarding
        // the error made "the shop wifi is down" indistinguishable from "you
        // are signed out" — and on a tablet that slept through lunch, the wifi
        // case is the likely one. It is retryable; say so and hold the clip.
        const { data: sess, error: sessionError } = await supabase.auth.getSession();
        const token = sess.session?.access_token;
        if (!token) {
          const reachable = !sessionError;
          setError(sessionError ? sessionError.message : 'Not signed in');
          setState('error');
          return {
            text: null,
            message: reachable
              ? 'Sign in expired — refresh the page.'
              : "Couldn't reach the shop to write that down. Your recording is saved — try again.",
            // The words are lost either way, so a send must not go ahead
            // without them. Whether a retry can help is a separate question —
            // which is exactly why these are two flags.
            lostWords: true,
            canRetry: !reachable,
          };
        }

        const ext = mime.includes('mp4') ? 'mp4' : 'webm';
        const form = new FormData();
        form.append('audio', blob, `voice.${ext}`);
        // Pin the language so a short / noisy clip doesn't get auto-detected
        // as Korean / Japanese (Whisper's failure mode on brief English).
        form.append('language', language);

        const resp = await fetch('/api/voice/transcribe', {
          method: 'POST',
          headers: { authorization: `Bearer ${token}` },
          body: form,
        });
        if (!resp.ok) {
          const t = await resp.text().catch(() => '');
          throw new Error(`Transcribe failed (${resp.status}): ${t.slice(0, 200)}`);
        }
        const json = (await resp.json()) as TranscribeResponse;
        setState('idle');
        // Whisper returns an empty string both for a genuinely quiet room and
        // for audio it simply could not make out — a loud bay, a low gain, a
        // muted input. Judging by the transcript treated the second as the
        // first: it destroyed the clip, blamed the tech for not speaking up,
        // and let the half message send. Judge by how long they talked for.
        if (!json.text || !json.text.trim()) {
          if (durationMs >= PROBABLY_SILENCE_MS) {
            return {
              text: null,
              message:
                "Couldn't turn that into words just now. Your recording is saved — try again.",
              lostWords: true,
              canRetry: true,
            };
          }
          releaseClip();
          return { text: null, message: 'Didn’t catch that — try again, a little louder.' };
        }
        releaseClip();
        return { text: json.text };
      } catch (err) {
        // Detail to the console for support; shop English to the tech.
        console.error('[voice] transcribe failed:', err);
        setError(err instanceof Error ? err.message : String(err));
        setState('error');
        return {
          text: null,
          message: describeTranscribeFailure(err),
          lostWords: true,
          canRetry: true,
        };
      }
    },
    [releaseClip, language],
  );

  const stopAndTranscribe = useCallback(async (): Promise<TranscribeOutcome> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') {
      return { text: null };
    }

    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      recorder.stop();
    });

    const mime = recorder.mimeType || 'audio/webm';
    const spokenMs = Date.now() - startedAtRef.current;
    const blob = new Blob(chunksRef.current, { type: mime });
    // Hold the clip BEFORE cleanup, and leave cleanup exactly where it is — it
    // stops the microphone tracks and the timer, and deferring it would leave
    // the mic hot and the red recording strip lit through the whole upload.
    heldClipRef.current = { blob, mime, durationMs: spokenMs };
    setHasHeldClip(true);
    cleanup();
    if (blob.size === 0) {
      releaseClip();
      setState('idle');
      return { text: null, message: 'Didn’t catch that — try again, a little louder.' };
    }

    return uploadClip(blob, mime, spokenMs);
  }, [cleanup, releaseClip, uploadClip]);

  const retryTranscribe = useCallback(async (): Promise<TranscribeOutcome> => {
    const held = heldClipRef.current;
    // Never message-less: a silent no-op here reads as a dead button, and the
    // most natural response to "try again" is tapping the mic, which is what
    // released the clip in the first place.
    if (!held) return { text: null, message: 'That recording is gone — say it again.' };
    return uploadClip(held.blob, held.mime, held.durationMs);
  }, [uploadClip]);

  const cancel = useCallback(() => {
    const r = recorderRef.current;
    if (r && r.state === 'recording') r.stop();
    cleanup();
    releaseClip();
    setState('idle');
    setDurationMs(0);
  }, [cleanup, releaseClip]);

  return {
    state,
    durationMs,
    error,
    hasHeldClip,
    start,
    stopAndTranscribe,
    retryTranscribe,
    discardClip: releaseClip,
    cancel,
  };
}
